import type { UpcomingInvoicesReportItem } from '@vy/types';
import type { Workbook, Worksheet } from 'exceljs';
import dayjs from 'dayjs';
import type { FilterField, FilterLineType } from '../../../../../components/new-design/vyTableFilter';
import { applyBillingForecastSearch } from '../../../billing-forecast-report-page/utils/apply-billing-forecast-search';
import {
  computeMonthlyGrandTotal,
  filterBillingForecastDateHeaders,
  getTotalBillingForecastDateHeaders,
} from '../../../billing-forecast-report-page/billing-forecast-report-components/BillingForecastReportTable/billing-forecast-report-calculations';
import {
  buildBillingForecastSummary,
} from '../../../billing-forecast-report-page/billing-forecast-report-components/BillingForecastReportTable/billing-forecast-report-aggregator';
import { buildAppliedFiltersSheet } from '../customer-revenue-report-utils/build-applied-filters-sheet';

export type BuildBillingForecastExportInput = {
  filteredReportItems: UpcomingInvoicesReportItem[];
  searchText: string | null | undefined;
  fromDate: Date | null;
  toDate: Date | null;
  filters: FilterLineType[];
  filterFields: FilterField[];
};

export function buildBillingForecastExport(
  workbook: Workbook,
  input: BuildBillingForecastExportInput,
): void {
  for (const worksheet of [...workbook.worksheets]) {
    workbook.removeWorksheet(worksheet.id);
  }

  const searchedItems = applyBillingForecastSearch(input.filteredReportItems, input.searchText);
  const allDateHeaders = getTotalBillingForecastDateHeaders(input.filteredReportItems);
  const dateHeaders = filterBillingForecastDateHeaders(allDateHeaders, input.fromDate, input.toDate);

  buildBillingForecastSheet(workbook, searchedItems, dateHeaders);
  buildInvoicesSheet(workbook, searchedItems);
  buildAppliedFiltersSheet(workbook, {
    searchText: input.searchText,
    fromDate: input.fromDate,
    recognizedUpTo: input.toDate,
    filters: input.filters,
    filterFields: input.filterFields,
  });
}

function buildBillingForecastSheet(
  workbook: Workbook,
  items: UpcomingInvoicesReportItem[],
  dateHeaders: string[],
): void {
  const worksheet = workbook.addWorksheet('Billing Forecast');
  const allHeaders = ['Customer', ...dateHeaders, 'Total'];

  writeBillingForecastHeader(worksheet, allHeaders);
  writeBillingForecastRows(worksheet, items, dateHeaders);
  writeBillingForecastTotalsRow(worksheet, items, dateHeaders);
  applyBillingForecastColumnStyles(worksheet, dateHeaders);
}

function writeBillingForecastHeader(worksheet: Worksheet, allHeaders: string[]): void {
  worksheet.addRow(allHeaders);
  worksheet.getRow(1).font = { bold: true };
  worksheet.views = [{ state: 'frozen', xSplit: 1, ySplit: 1 }];
  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: allHeaders.length },
  };
}

function writeBillingForecastRows(
  worksheet: Worksheet,
  items: UpcomingInvoicesReportItem[],
  dateHeaders: string[],
): void {
  const summaryRows = buildBillingForecastSummary(items);

  for (const summaryRow of summaryRows) {
    const dateValues = dateHeaders.map((header) => {
      const monthTotal = summaryRow.monthlyTotals.get(header) ?? 0;
      return nullIfZero(monthTotal);
    });

    const excelRow = worksheet.addRow([
      summaryRow.customer.name,
      ...dateValues,
      nullIfZero(summaryRow.total),
    ]);
    excelRow.commit();
  }
}

function writeBillingForecastTotalsRow(
  worksheet: Worksheet,
  items: UpcomingInvoicesReportItem[],
  dateHeaders: string[],
): void {
  const dateValues = dateHeaders.map((header) => nullIfZero(computeMonthlyGrandTotal(items, header)));
  const grandTotal = nullIfZero(items.reduce((sum, item) => sum + item.revenue.total, 0));

  const totalsRow = worksheet.addRow(['Total', ...dateValues, grandTotal]);
  totalsRow.font = { bold: true };
  totalsRow.commit();
}

function applyBillingForecastColumnStyles(worksheet: Worksheet, dateHeaders: string[]): void {
  worksheet.getColumn(1).width = 28;

  for (let index = 0; index < dateHeaders.length; index++) {
    const column = worksheet.getColumn(index + 2);
    column.width = 16;
    column.numFmt = '#,##0.00';
    column.alignment = { horizontal: 'right' };
  }

  const totalColumn = worksheet.getColumn(dateHeaders.length + 2);
  totalColumn.width = 16;
  totalColumn.numFmt = '#,##0.00';
  totalColumn.alignment = { horizontal: 'right' };
}

const INVOICES_SHEET_HEADERS = [
  'Customer Name',
  'Contract Status',
  'Invoice Name',
  'Invoicing Date',
  'Period Start',
  'Period End',
  'Currency',
  'Review Status',
  'Billing Status',
  'Total',
] as const;

function buildInvoicesSheet(
  workbook: Workbook,
  items: UpcomingInvoicesReportItem[],
): void {
  const worksheet = workbook.addWorksheet('Invoices');

  writeInvoicesHeader(worksheet);
  writeInvoicesRows(worksheet, items);
  applyInvoicesColumnStyles(worksheet);
}

function writeInvoicesHeader(worksheet: Worksheet): void {
  worksheet.addRow([...INVOICES_SHEET_HEADERS]);
  worksheet.getRow(1).font = { bold: true };
  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: INVOICES_SHEET_HEADERS.length },
  };
}

function writeInvoicesRows(worksheet: Worksheet, items: UpcomingInvoicesReportItem[]): void {
  for (const item of items) {
    for (const invoice of item.invoices) {
      const invoicingDateLabel = dayjs(invoice.invoicingDate).format('MMM YYYY');
      const periodStart = dayjs(invoice.period.startTime).format('MMM D, YYYY');
      const periodEnd = dayjs(invoice.period.endTime).format('MMM D, YYYY');

      const excelRow = worksheet.addRow([
        item.customer.name,
        item.contract.status,
        invoice.name,
        invoicingDateLabel,
        periodStart,
        periodEnd,
        item.revenue.currency,
        invoice.reviewStatus,
        invoice.billingStatus,
        invoice.total,
      ]);
      excelRow.commit();
    }
  }
}

function applyInvoicesColumnStyles(worksheet: Worksheet): void {
  const widths = [28, 18, 28, 16, 16, 16, 12, 18, 18, 16];
  for (let index = 0; index < widths.length; index++) {
    worksheet.getColumn(index + 1).width = widths[index];
  }
  const totalColumn = worksheet.getColumn(INVOICES_SHEET_HEADERS.length);
  totalColumn.numFmt = '#,##0.00';
  totalColumn.alignment = { horizontal: 'right' };
}

function nullIfZero(value: number): number | null {
  return value === 0 ? null : value;
}
